import { useState, useEffect, useCallback, useRef } from 'react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import { expValEquals } from '@confluence/feature-experiments';

const AGENTIC_MODE_CSS = `
/* ===== AGENTIC EDITOR MODE STYLES ===== */
/* Disable all toolbar buttons */
[data-agentic-mode="true"] [data-testid="ak-editor-main-toolbar"] button { pointer-events: none !important; opacity: 0.3 !important; }
/* Re-enable: Undo button */
[data-agentic-mode="true"] [data-testid="ak-editor-main-toolbar"] button[data-testid="ak-editor-toolbar-button-undo"] { pointer-events: auto !important; opacity: 1 !important; }
/* Re-enable: Redo button */
[data-agentic-mode="true"] [data-testid="ak-editor-main-toolbar"] button[data-testid="ak-editor-toolbar-button-redo"] { pointer-events: auto !important; opacity: 1 !important; }
/* Re-enable: Write (Ask Rovo) button + glow */
[data-agentic-mode="true"] [data-testid="ak-editor-main-toolbar"] button[data-testid="editor-toolbar__ask-rovo"],
[data-agentic-mode="true"] [data-testid="ak-editor-main-toolbar"] [data-testid="editor-toolbar__ask-rovo"] { pointer-events: auto !important; opacity: 1 !important; animation: agenticButtonGlow 3s ease-in-out infinite !important; border-radius: 4px !important; }
/* Re-enable: Ask Rovo dropdown menu */
[data-agentic-mode="true"] [data-testid="ak-editor-main-toolbar"] button[data-testid="editor-toolbar__ask-rovo-menu"],
[data-agentic-mode="true"] [data-testid="ak-editor-main-toolbar"] button[data-testid="editor-toolbar__ask-rovo-menu-display-small"] { pointer-events: auto !important; opacity: 1 !important; }
/* Re-enable: Review (AI suggestions) button + glow */
[data-agentic-mode="true"] [data-testid="ak-editor-main-toolbar"] button[data-testid="editor-toolbar__ai-suggestions-button"],
[data-agentic-mode="true"] [data-testid="ak-editor-main-toolbar"] [data-testid="editor-toolbar__ai-suggestions-button"] { pointer-events: auto !important; opacity: 1 !important; animation: agenticButtonGlow 3s ease-in-out infinite !important; border-radius: 4px !important; }
/* Re-enable: Improve writing */
[data-agentic-mode="true"] [data-testid="ak-editor-main-toolbar"] button[data-testid="editor-toolbar__improve-writing"] { pointer-events: auto !important; opacity: 1 !important; }
/* Re-enable: Remix */
[data-agentic-mode="true"] [data-testid="ak-editor-main-toolbar"] button[data-testid="editor-toolbar__remix-button"] { pointer-events: auto !important; opacity: 1 !important; }
/* Re-enable: Comment button */
[data-agentic-mode="true"] [data-testid="ak-editor-main-toolbar"] button[data-testid="ak-editor-annotation-toolbar-create-button"] { pointer-events: auto !important; opacity: 1 !important; }
/* Re-enable: AI/Rovo buttons by aria-label pattern */
[data-agentic-mode="true"] [data-testid="ak-editor-main-toolbar"] button[data-testid^="ak-editor-ai-"],
[data-agentic-mode="true"] [data-testid="ak-editor-main-toolbar"] button[aria-label*="Rovo"],
[data-agentic-mode="true"] [data-testid="ak-editor-main-toolbar"] button[aria-label*="AI"] { pointer-events: auto !important; opacity: 1 !important; }
/* Re-enable: AI/Rovo buttons in floating toolbar (portals render outside wrapper — scoped via body:has) */
body:has([data-agentic-mode="true"]) [data-testid="editor-toolbar__ask-rovo"],
body:has([data-agentic-mode="true"]) [data-testid="editor-toolbar__ask-rovo"] *,
body:has([data-agentic-mode="true"]) [data-testid="editor-toolbar__ask-rovo-menu"],
body:has([data-agentic-mode="true"]) [data-testid="editor-toolbar__ask-rovo-menu"] *,
body:has([data-agentic-mode="true"]) [data-testid="editor-toolbar__ask-rovo-menu-display-small"],
body:has([data-agentic-mode="true"]) [data-testid="editor-toolbar__ask-rovo-menu-display-small"] *,
body:has([data-agentic-mode="true"]) [data-testid="editor-toolbar__ai-suggestions-button"],
body:has([data-agentic-mode="true"]) [data-testid="editor-toolbar__ai-suggestions-button"] *,
body:has([data-agentic-mode="true"]) [data-testid="editor-toolbar__improve-writing"],
body:has([data-agentic-mode="true"]) [data-testid="editor-toolbar__improve-writing"] *,
body:has([data-agentic-mode="true"]) [data-testid="editor-toolbar__remix-button"],
body:has([data-agentic-mode="true"]) [data-testid="editor-toolbar__remix-button"] *,
body:has([data-agentic-mode="true"]) [data-testid="ak-editor-annotation-toolbar-create-button"],
body:has([data-agentic-mode="true"]) [data-testid="ak-editor-annotation-toolbar-create-button"] *,
body:has([data-agentic-mode="true"]) [data-testid="atlassian-intelligence-toolbar-button"],
body:has([data-agentic-mode="true"]) [data-testid="atlassian-intelligence-toolbar-button"] *,
body:has([data-agentic-mode="true"]) button[data-testid="editor-toolbar__ask-rovo"],
body:has([data-agentic-mode="true"]) button[data-testid="editor-toolbar__ai-suggestions-button"],
body:has([data-agentic-mode="true"]) button[data-testid="atlassian-intelligence-toolbar-button"] { pointer-events: auto !important; opacity: 1 !important; }
/* Glow animation - solid purple pulse */
@keyframes agenticButtonGlow {
0%, 100% { box-shadow: 0 0 6px 2px rgba(130, 112, 219, 0.3), 0 0 12px 4px rgba(130, 112, 219, 0.15); }
50% { box-shadow: 0 0 12px 4px rgba(130, 112, 219, 0.5), 0 0 24px 8px rgba(130, 112, 219, 0.25); }
}
/* Accessibility: respect prefers-reduced-motion (WCAG 2.1 SC 2.3.3) — suppress animation entirely */
@media (prefers-reduced-motion: reduce) {
[data-agentic-mode="true"] [data-testid="ak-editor-main-toolbar"] [data-testid="editor-toolbar__ask-rovo"],
[data-agentic-mode="true"] [data-testid="ak-editor-main-toolbar"] [data-testid="editor-toolbar__ai-suggestions-button"],
body:has([data-agentic-mode="true"]) button:has(span[aria-label="Rovo"]) { animation: none !important; }
}
/* Glow the whole selection floating toolbar (portalled outside editor wrapper — scoped via body:has) */
body:has([data-agentic-mode="true"]) [data-testid="editor-floating-toolbar"][aria-label="Selection toolbar"] { box-shadow: 0 0 12px 4px rgba(130, 112, 219, 0.4), 0 0 24px 8px rgba(130, 112, 219, 0.2); border-radius: 8px; }
/* Ask Rovo button glow (top-right page button — outside editor wrapper, scoped via body:has) */
body:has([data-agentic-mode="true"]) button:has(span[aria-label="Rovo"]) { animation: agenticButtonGlow 3s ease-in-out infinite !important; border-radius: 4px !important; }
/* Hide quick insert (+) button */
[data-agentic-mode="true"] [data-testid="block-ctrl-quick-insert-button"], [data-agentic-mode="true"] button[data-testid="editor-quick-insert-button"] { display: none !important; }
/* Remove glow from inline AI button in selection toolbar (whole toolbar already glows — portalled, scoped via body:has) */
body:has([data-agentic-mode="true"]) button[data-testid="ak-editor-ai-selection-toolbar-ai-button"] { animation: none !important; box-shadow: none !important; }
/* Hide drag handle */
[data-agentic-mode="true"] [data-testid="block-ctrl-drag-handle-container"] { display: none !important; }
`;

/**
 * Checks if the event target is inside the main ProseMirror editor content area.
 * Excludes AI modals, AI editors, Rovo prompt, and comment editors.
 */
export const isMainEditorContent = (target: EventTarget | null): boolean => {
	if (!(target instanceof HTMLElement)) return false;
	if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA') return false;
	if (target.closest('[data-testid="ak-editor-plugin-ai-modal"]')) return false;
	if (target.closest('[data-testid^="ak-editor-ai"]')) return false;
	if (target.closest('[data-testid="rovo-prompt-session-screen"]')) return false;
	if (target.closest('[data-testid="chromeless-editor"]')) return false;
	return target.closest('.ProseMirror') !== null;
};

export const useAgenticEditorMode = () => {
	const [isAgenticMode, setIsAgenticMode] = useState(false);
	const agenticEditorRef = useRef<HTMLDivElement | null>(null);
	const agenticModeStartTime = useRef<number>(0);
	const { createAnalyticsEvent } = useAnalyticsEvents();

	// Check experiment on mount (SSR-safe) and fire entered event for post-experiment analysis
	useEffect(() => {
		if (expValEquals('confluence_agentic_editor_mode', 'isEnabled', true)) {
			agenticModeStartTime.current = Date.now();
			setIsAgenticMode(true);
			// Wrap analytics fire() in try/catch so a broken analytics setup can't prevent
			// the editor from loading. By the time we reach this line, the user is already
			// in agentic mode — we must not let analytics errors crash the editor on mount.
			try {
				createAnalyticsEvent({
					type: 'sendTrackEvent',
					data: {
						action: 'entered',
						actionSubject: 'agenticEditorMode',
						source: 'agenticEditorMode',
						attributes: {
							experimentName: 'confluence_agentic_editor_mode',
						},
					},
				}).fire();
			} catch {
				// Swallow analytics errors — agentic mode should still work
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const handleExitAgenticMode = useCallback(() => {
		setIsAgenticMode(false);
		agenticModeStartTime.current = 0;
	}, []);

	// Inject agentic mode CSS into document head
	useEffect(() => {
		if (!isAgenticMode) return;
		const style = document.createElement('style');
		style.setAttribute('data-agentic-mode-styles', '');
		style.textContent = AGENTIC_MODE_CSS;
		document.head.appendChild(style);
		return () => {
			style.remove();
		};
	}, [isAgenticMode]);

	// Block keyboard/input/clipboard/drag events in the main editor
	useEffect(() => {
		if (!isAgenticMode) return;
		const container = agenticEditorRef.current;
		if (!container) return;

		const handleKeyDown = (e: KeyboardEvent) => {
			if (!isMainEditorContent(e.target)) return;
			// Allow: Ctrl/Cmd+C (copy), +A (select all), +Z (undo), +Y (redo), +F (browser find)
			const allowedKeys = ['c', 'a', 'z', 'y', 'f'];
			const isModifierCombo = (e.ctrlKey || e.metaKey) && allowedKeys.includes(e.key.toLowerCase());
			const isNavigationKey = [
				'ArrowUp',
				'ArrowDown',
				'ArrowLeft',
				'ArrowRight',
				'Home',
				'End',
				'PageUp',
				'PageDown',
				'Escape',
			].includes(e.key);
			const isModifierOnly = ['Shift', 'Control', 'Alt', 'Meta'].includes(e.key);
			const isFunctionKey =
				e.key.length >= 2 &&
				e.key.length <= 3 &&
				e.key.startsWith('F') &&
				!isNaN(Number(e.key.slice(1)));
			if (!isModifierCombo && !isNavigationKey && !isModifierOnly && !isFunctionKey) {
				e.preventDefault();
				e.stopPropagation();
			}
		};
		// Shared handler for beforeinput/paste/cut/drop/dragstart/compositionstart — all block input in main editor
		const blockIfMainEditor = (e: Event) => {
			if (isMainEditorContent(e.target)) {
				e.preventDefault();
				e.stopPropagation();
			}
		};

		container.addEventListener('keydown', handleKeyDown as EventListener, true);
		container.addEventListener('beforeinput', blockIfMainEditor, true);
		container.addEventListener('paste', blockIfMainEditor, true);
		container.addEventListener('cut', blockIfMainEditor, true);
		// Block IME composition (CJK keyboards bypass keydown/beforeinput via composition events)
		container.addEventListener('compositionstart', blockIfMainEditor, true);
		// eslint-disable-next-line @atlaskit/design-system/no-direct-use-of-web-platform-drag-and-drop -- blocking drag in agentic mode requires native event listeners
		container.addEventListener('drop', blockIfMainEditor, true);
		// eslint-disable-next-line @atlaskit/design-system/no-direct-use-of-web-platform-drag-and-drop -- blocking drag in agentic mode requires native event listeners
		container.addEventListener('dragstart', blockIfMainEditor, true);

		return () => {
			container.removeEventListener('keydown', handleKeyDown as EventListener, true);
			container.removeEventListener('beforeinput', blockIfMainEditor, true);
			container.removeEventListener('paste', blockIfMainEditor, true);
			container.removeEventListener('cut', blockIfMainEditor, true);
			container.removeEventListener('compositionstart', blockIfMainEditor, true);
			container.removeEventListener('drop', blockIfMainEditor, true);
			container.removeEventListener('dragstart', blockIfMainEditor, true);
		};
	}, [isAgenticMode]);

	return {
		isAgenticMode,
		handleExitAgenticMode,
		agenticEditorRef,
		agenticModeStartTime: agenticModeStartTime.current,
	};
};
